const normalizeName = value =>
  String(value || '')
    .trim()
    .toLowerCase();

const slugifyStationPath = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const uniqueBy = (records, getKey) => {
  const seen = new Set();

  return records.filter(record => {
    const key = getKey(record);
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

export const getPersonName = record =>
  record.userName ||
  record.operatorName ||
  record.employeeName ||
  record.userFullName ||
  (record.userId ? `User ${record.userId}` : 'Unassigned');

export const getRecordUnitCount = record =>
  Number.isFinite(Number(record.outputCount)) ? Number(record.outputCount) : 1;

export const getBoxesAndItems = records =>
  records.reduce(
    (acc, record) => {
      const isBox = record.isBox ?? record.IsBox;
      if (typeof isBox !== 'boolean') {
        return acc;
      }

      acc.hasBoxData = true;
      const unit = getRecordUnitCount(record);
      if (isBox) {
        acc.boxesCount += unit;
      } else {
        acc.itemsCount += unit;
      }

      return acc;
    },
    { hasBoxData: false, itemsCount: 0, boxesCount: 0 }
  );

export const getRecordCount = (records, metricType) => {
  if (!records.length) {
    return 0;
  }

  if (metricType === 'sashes') {
    const sashRecords = records.filter(record => Number(record.sashNo) > 0);

    if (sashRecords.length) {
      return uniqueBy(
        sashRecords,
        record => `${record.headerId}-${record.frameId}-${record.sashNo}`
      ).length;
    }
  }

  return records.reduce((total, record) => total + getRecordUnitCount(record), 0);
};

export const groupRecords = (records, groupBy, metricType) => {
  const grouped = records.reduce((acc, record) => {
    const key = groupBy(record);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(record);
    return acc;
  }, {});

  return Object.keys(grouped)
    .map(name => ({
      name,
      value: getRecordCount(grouped[name], metricType)
    }))
    .sort((a, b) => b.value - a.value);
};

export const groupRecordsWithItemTypeDetail = (records, groupBy, metricType, itemTypeRecords = []) => {
  const grouped = records.reduce((acc, record) => {
    const key = groupBy(record);
    if (!acc[key]) acc[key] = [];
    acc[key].push(record);
    return acc;
  }, {});

  const itemTypeGrouped = itemTypeRecords.reduce((acc, record) => {
    const key = groupBy(record);
    if (!acc[key]) acc[key] = [];
    acc[key].push(record);
    return acc;
  }, {});

  return Object.keys(grouped)
    .map(name => {
      const groupedRecs = grouped[name];
      const hasItemType = groupedRecs.some(r => r.itemType || r.ItemType || r.itemTypeName || r.ItemTypeName);

      let itemTypeDetail = null;
      if (hasItemType) {
        itemTypeDetail = getItemTypeBreakdown(groupedRecs);
      } else if (itemTypeGrouped[name]?.length) {
        itemTypeDetail = getItemTypeBreakdown(itemTypeGrouped[name]);
      }

      return {
        name,
        value: getRecordCount(groupedRecs, metricType),
        itemTypeDetail
      };
    })
    .sort((a, b) => b.value - a.value);
};

const getItemType = record =>
  record.itemType ||
  record.ItemType ||
  record.itemTypeName ||
  record.ItemTypeName ||
  'Other';

export const getItemTypeBreakdown = records =>
  groupRecords(records, getItemType, 'items');

export const groupBoxRecords = (records, groupBy) => {
  const grouped = records.reduce((acc, record) => {
    const key = groupBy(record);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(record);
    return acc;
  }, {});

  return Object.keys(grouped)
    .map(name => {
      const summary = getBoxesAndItems(grouped[name]);
      const value = summary.itemsCount + summary.boxesCount;

      return {
        name,
        value,
        hasBoxData: summary.hasBoxData,
        itemsCount: summary.itemsCount,
        boxesCount: summary.boxesCount
      };
    })
    .sort((a, b) => b.value - a.value);
};

export const flattenWeldedCillSource = records =>
  (Array.isArray(records) ? records : [])
    .filter(record => record)
    .map(record => {
      const stationName = record.stationName ?? record.StationName;
      const stationPath = slugifyStationPath(stationName);
      const shift = record.shift ?? record.Shift;
      const userId = record.userId ?? record.UserId;
      const boxId = record.boxId ?? record.BoxId;
      const totalFacets = Number(record.totalFacets ?? record.TotalFacets) || 0;

      return {
        ...record,
        stationName,
        stationPath,
        shift,
        userId,
        boxId,
        totalFacets,
        normalizedStationName: normalizeName(stationName),
        normalizedStationPath: normalizeName(slugifyStationPath(stationPath)),
        sourceType: 'weldedCill',
        source: 'api',
        isMock: false
      };
    })
    .filter(record => record.stationName || record.stationPath);

export const getWeldedCillStats = records => {
  const uniqueBoxIds = new Set(records.map(r => r.boxId).filter(id => id != null));
  const totalFacets = records.reduce((sum, r) => sum + r.totalFacets, 0);
  return { boxesCreated: uniqueBoxIds.size, totalFacets };
};


export const groupWeldedCillRecords = (records, groupBy) => {
  const grouped = records.reduce((acc, record) => {
    const key = groupBy(record);
    if (!acc[key]) acc[key] = [];
    acc[key].push(record);
    return acc;
  }, {});

  return Object.keys(grouped)
    .map(name => {
      const stats = getWeldedCillStats(grouped[name]);
      return {
        name,
        value: stats.boxesCreated,
        hasWeldedCillData: true,
        weldedCillBoxesCreated: stats.boxesCreated,
        weldedCillTotalFacets: stats.totalFacets
      };
    })
    .sort((a, b) => b.value - a.value);
};


export const flattenSource = (records, sourceType) =>
  (Array.isArray(records) ? records : [])
    .filter(record => record)
    .map(record => {
      const stationName = record.stationName ?? record.StationName;
      const stationPath =
        record.stationPath ??
        record.StationPath ??
        slugifyStationPath(stationName);
      const shift = record.shift ?? record.Shift;
      const outputCount = record.outputCount ?? record.OutputCount;
      const userId = record.userId ?? record.UserId;

      return {
        ...record,
        stationName,
        stationPath,
        shift,
        outputCount,
        userId,

        // Normalized fields used for station matching.
        normalizedStationName: normalizeName(stationName),
        normalizedStationPath: normalizeName(slugifyStationPath(stationPath)),

        sourceType,
        source: 'api',
        isMock: false
      };
    })
    .filter(record => record.stationName || record.stationPath);

export const getMatchingRecords = (records, stationConfig) => {
  const stationName = normalizeName(stationConfig.name);
  const stationPath = normalizeName(slugifyStationPath(stationConfig.stationPath));
  const inferredStationPath = normalizeName(slugifyStationPath(stationConfig.name));

  return records.filter(record =>
    stationPath
      ? record.normalizedStationPath === stationPath
      : record.normalizedStationPath === inferredStationPath ||
        record.normalizedStationName === stationName
  );
};
